import {ActionType, createReducer} from 'typesafe-actions';
import {actions} from '../../../index';
import {
    AdapterHealth,
    Agent,
    AiAgentsResponse,
    AssignAgentPhoneError,
    TenantSession,
    VoiceAiConfig,
} from '../../../types/AiAgent';
import {APIErrorInterface} from '../../../types';

export type AiAgentStateType = {
    adapterHealth?: AdapterHealth;
    adapterHealthLoading?: boolean;
    adapterHealthError?: APIErrorInterface;

    voiceAiConfig?: VoiceAiConfig | null;
    voiceAiConfigLoading?: boolean;

    tenant?: TenantSession;
    tenantLoading?: boolean;
    tenantError?: APIErrorInterface;

    aiAgentsList?: AiAgentsResponse;
    aiAgentsListLoading?: boolean;
    aiAgentsListLoadingError?: {error: string};

    assignAgentPhoneLoading?: boolean;
    assignAgentPhoneError?: AssignAgentPhoneError;

    matchedAiAgent?: Agent | null;
    matchedAiAgentLoading?: boolean;
    matchedAiAgentError?: APIErrorInterface;
};

export const initialState: AiAgentStateType = {
    adapterHealth: undefined,
    adapterHealthLoading: undefined,
    adapterHealthError: undefined,
    voiceAiConfig: undefined,
    voiceAiConfigLoading: undefined,
    tenant: undefined,
    tenantLoading: undefined,
    tenantError: undefined,
    aiAgentsList: undefined,
    aiAgentsListLoading: false,
    aiAgentsListLoadingError: undefined,
    assignAgentPhoneLoading: undefined,
    assignAgentPhoneError: undefined,
    matchedAiAgent: undefined,
    matchedAiAgentLoading: undefined,
    matchedAiAgentError: undefined,
};

export type aiAgentActions = ActionType<typeof actions>;

// @ts-ignore
export const aiAgentReducer = createReducer<AiAgentStateType, aiAgentActions>(initialState)
    .handleAction(actions.getAdapterHealth.request, (state) => ({
        ...state,
        adapterHealthLoading: true,
        adapterHealthError: undefined,
    }))
    .handleAction(actions.getAdapterHealth.success, (state, action) => ({
        ...state,
        adapterHealthLoading: false,
        adapterHealth: action.payload,
    }))
    .handleAction(actions.getAdapterHealth.failure, (state, action) => ({
        ...state,
        adapterHealthLoading: false,
        adapterHealth: undefined,

        adapterHealthError: action.payload ?? {
            faultcode: 'adapter_health_failed',
            faultstring: 'adapter health check failed',
        },
    }))

    .handleAction(actions.getVoiceAiConfig.request, (state) => ({
        ...state,
        voiceAiConfigLoading: true,
    }))
    .handleAction(actions.getVoiceAiConfig.success, (state, action) => ({
        ...state,
        voiceAiConfigLoading: false,
        voiceAiConfig: action.payload,
    }))
    .handleAction(actions.getVoiceAiConfig.failure, (state) => ({
        ...state,
        voiceAiConfigLoading: false,
        voiceAiConfig: null,
    }))

    .handleAction(actions.updateVoiceAiConfig.success, (state, action) => ({
        ...state,
        voiceAiConfig: action.payload,
    }))

    .handleAction(actions.refreshTenant.request, (state) => ({
        ...state,
        tenantLoading: true,
        tenantError: undefined,
    }))
    .handleAction(actions.refreshTenant.success, (state, action) => ({
        ...state,
        tenantLoading: false,
        tenant: action.payload,
    }))
    .handleAction(actions.refreshTenant.failure, (state, action) => ({
        ...state,
        tenantLoading: false,
        tenant: undefined,
        // Always store a truthy error. A network/CORS/5xx-without-body failure carries no
        // response.data, so without this fallback tenantError stays undefined — the AiTab
        // guard never engages and refreshTenant.request is re-dispatched forever (ICAP).
        tenantError: action.payload ?? {
            faultcode: 'init_session_failed',
            faultstring: 'init-session failed',
        },
    }))

    .handleAction(actions.getAiAgentsList.request, (state) => ({
        ...state,
        aiAgentsListLoading: true,
        aiAgentsListLoadingError: undefined,
    }))
    .handleAction(actions.getAiAgentsList.success, (state, action) => ({
        ...state,
        aiAgentsListLoading: false,
        aiAgentsList: action.payload,
    }))
    .handleAction(actions.getAiAgentsList.failure, (state, action) => ({
        ...state,
        aiAgentsListLoading: false,
        aiAgentsListLoadingError: action.payload as {error: string},
    }))

    .handleAction(actions.assignAgentPhone.request, (state) => ({
        ...state,
        assignAgentPhoneLoading: true,
        assignAgentPhoneError: undefined,
    }))
    .handleAction(actions.assignAgentPhone.success, (state) => ({
        ...state,
        assignAgentPhoneLoading: false,
    }))
    .handleAction(actions.assignAgentPhone.failure, (state, action) => ({
        ...state,
        assignAgentPhoneLoading: false,
        assignAgentPhoneError: action.payload,
    }))
    .handleAction(actions.clearAssignAgentPhoneError, (state) => ({
        ...state,
        assignAgentPhoneError: undefined,
    }))
    .handleAction(actions.resetAiAgentState, (state) => ({...state, ...initialState}))
    .handleAction(actions.findAiAgentBySipUri.request, (state) => ({
        ...state,
        matchedAiAgentLoading: true,
        matchedAiAgentError: undefined,
    }))
    .handleAction(actions.findAiAgentBySipUri.success, (state, action) => ({
        ...state,
        matchedAiAgentLoading: false,
        matchedAiAgent: action.payload,
    }))
    .handleAction(actions.findAiAgentBySipUri.failure, (state, action) => ({
        ...state,
        matchedAiAgentLoading: false,
        matchedAiAgent: null,
        matchedAiAgentError: action.payload,
    }));

export default aiAgentReducer;
